import { Card, CircularProgress, IconButton, List, ListItem, ListItemButton, ListItemText, Alert, Button } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react'
import ModalContext from '../../../context/ModalContext';
import GetTimeFormatted from '../../../helper/getTimeFormatted';

export default function ConfirmCoupon({ selectedCompany, couponDealOption, couponDetails, orderedCategoriesData, termsConditions, instructions, locationDetails, isExpired, couponId, onBack }) {

  const [selectedLocations, selectLocations] = useState([]);
  const modalCtx = useContext(ModalContext);

  useEffect(() => {
    selectLocations(locationDetails?.locations.filter(lc => locationDetails?.checkedId?.includes(lc.id)))
  }, [locationDetails])

  const getExpirationOption = (selectedButton) => {

    switch (selectedButton) {
      case 1:
        return "Weeks";
      case 2:
        return "Months";
      case 3:
        return "Days";
      case 4:
        return "Years";
    }

  }

  const getSelectedDealOption = () => {

    if (couponDealOption.selectedDealOptions == 0) {
      return <div>
        <div className='big-detail-title white-color'>{couponDealOption.first.dealVal1}</div>
        <div className='detail-title white-color'>{couponDealOption.first.dealVal2}</div>
        <div className='detail-title white-color'>FREE</div>
      </div>
    } else if (couponDealOption.selectedDealOptions == 1) {
      return <div>
        <div className='big-detail-title white-color'>{couponDealOption.second.dealVal1}</div>
        <div className='detail-title white-color'>% OFF</div>
      </div>
    } else {
      return <div> <div className='big-detail-title white-color'>{couponDealOption.third.dealVal1}</div>
        <div className='detail-title white-color'>{couponDealOption.third.dealVal2}</div>
      </div>
    }
  }

  // Check if contact details are available
  const hasContactDetails = () => {
    return !!(couponDetails.couponClaimUrl || couponDetails.emailAddress || couponDetails.phoneNumber);
  };

  // Check if locations are available
  const hasLocations = () => {
    return selectedLocations && selectedLocations.length > 0;
  };

  // Check if coupon is global (online-only and globally available)
  const isGlobalCoupon = () => {
    return locationDetails?.onlineOnly === "yes" && locationDetails?.availabilityType === "global";
  };

  // Check if coupon is country-specific (online-only but local availability)
  const isCountrySpecificCoupon = () => {
    return locationDetails?.onlineOnly === "yes" && locationDetails?.availabilityType === "local";
  };

  // Get unique countries from selected locations
  const getUniqueCountries = () => {
    if (!selectedLocations || selectedLocations.length === 0) return [];
    
    const countries = selectedLocations
      .map(location => location.country)
      .filter((country, index, self) => country && self.indexOf(country) === index);
    
    return countries;
  };

  // Check if keywords are available
  const hasKeywords = () => {
    return couponDetails.keywords && couponDetails.keywords.length > 0;
  };

  // Calculate expiry date for display
  const getExpiryDate = () => {
    // If coupon doesn't expire, show "No expiry"
    if (couponDealOption.couponExpiration) {
      return "No expiry";
    }
    
    // For pending/approved coupons, show original validTill if available
    if (couponDealOption.validTill) {
      return GetTimeFormatted(couponDealOption.validTill, "DD/MM/YYYY");
    }
    
    // For new coupon creation, calculate live expiration date based on current settings
    if (couponDealOption.expirationButton && couponDealOption.expirationOption) {
      const currentDate = new Date();
      const expirationDate = new Date(currentDate);
      
      // Calculate expiration based on selected button and option
      if (couponDealOption.expirationButton === 1) { // SHORT TERM - weeks
        expirationDate.setDate(currentDate.getDate() + (couponDealOption.expirationOption * 7));
      } else if (couponDealOption.expirationButton === 2) { // MID TERM - months
        expirationDate.setMonth(currentDate.getMonth() + couponDealOption.expirationOption);
      } else if (couponDealOption.expirationButton === 3) { // SPECIAL OCCASIONS - days
        expirationDate.setDate(currentDate.getDate() + couponDealOption.expirationOption);
      } else if (couponDealOption.expirationButton === 4) { // LONG TERM - years
        expirationDate.setFullYear(currentDate.getFullYear() + couponDealOption.expirationOption);
      }
      
      return GetTimeFormatted(expirationDate, "DD/MM/YYYY");
    }
    
    return null;
  };

  const handleConfirmClick = () => {
    modalCtx.openModal();
    modalCtx.setDetails("coupon-status", { couponId });
  };

  return (
    <div className='create-coupon-section-v2'>
      {isExpired && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          This coupon has expired and cannot be confirmed. Please update the expiration date or set it to not expire.
        </Alert>
      )}
      <Card className='card-container' sx={{ width: '100%' }}>
        <span className='title'>Deal</span>
        <div className='three-col-container'>
          <div className='single-item right-border-item'>
            <span className='big-title'>{selectedCompany?.companyDetails?.company || 'Company Name'}</span>
            <span className='detail-title'>{couponDealOption?.couponExpiration ? 'Coupon does not expire' : `Offer is valid for ${couponDealOption.expirationOption} ${getExpirationOption(couponDealOption.expirationButton)}`}</span>
          </div>
          <div className='single-item right-border-item'>
            <Card className='card center-item logo-item'>
              {getSelectedDealOption()}
              {selectedCompany?.logo && <img src={selectedCompany.logo} className='logo-image'></img>}
            </Card>
          </div>

          <div className='single-item'>
            <span className='final-title'>Coupon Description</span>
            <span className='detail-title'>{couponDealOption.description}</span>
          </div>
        </div>
      </Card>
      
      {/* Only show coupon details if contact details are available */}
      {hasContactDetails() && (
        <Card className='card-container' sx={{ width: '100%' }}>
          <span className='title'>Coupon Details</span>
          <div className='three-col-container'>

            <div className='single-item right-container right-border-item'>
              {couponDetails.couponClaimUrl && (
                <>
                  <span className='final-title'>Website (Online Coupon URL)</span>
                  {couponDetails.couponClaimUrl.split("\n").map((item, index) => <span className='detail-title' key={index}>{item}</span>)}
                </>
              )}
              {(couponDetails.emailAddress || couponDetails.phoneNumber) && (
                <>
                  <span className='final-title'>Contact Details</span>
                  {couponDetails.emailAddress && couponDetails.emailAddress.split("\n").map((item, index) => <span className='detail-title' key={index}>{item}</span>)}
                  {couponDetails.phoneNumber && <span className='detail-title'>{couponDetails.phoneNumber}</span>}
                </>
              )}
            </div>

            <div className='single-item right-border-item'>
              <span className='final-title'>Coupon Code</span>
              <span className='big-detail-title'>{couponDetails.couponCode}</span>
              {getExpiryDate() && (
                <>
                  <span className='final-title' style={{ marginTop: '15px' }}>Expiry Date</span>
                  <span className='detail-title'>{getExpiryDate()}</span>
                </>
              )}
            </div>

            <div className='single-item'>
              <span className='final-title'>Categories</span>
              <div className='item-center-container'>
                {orderedCategoriesData?.filter((item) => couponDetails.categories.includes(item.id)).map((item, index) => <div key={index} className='selected-item-card'>{item.title}</div>)}
              </div>
              {/* Show keywords in the same section if available */}
              {hasKeywords() && (
                <>
                  <span className='final-title' style={{ marginTop: '15px' }}>Keywords</span>
                  <div className='item-center-container'>
                    {couponDetails.keywords.map((keyword, index) => (
                      <div key={index} className='selected-item-card'>{keyword}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className='horizontal-row-card-container'>
        <Card className='card-container' sx={{ width: '50%' }}>
          <span className='title'>Terms & Conditions</span>
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {termsConditions.map((value, index) => {
              const labelId = `checkbox-list-label-${value.id}`;
              if (value.checked) {
                return (
                  <ListItem
                    key={value.id}
                    secondaryAction={
                      <IconButton edge="end" aria-label="comments">
                      </IconButton>
                    }
                    disablePadding
                  >
                    <ListItemText id={labelId} primary={`${value.instruction}`} />
                  </ListItem>
                );
              }
            })}
          </List>
        </Card>
        
        {/* Show locations section with appropriate content */}
        {(isGlobalCoupon() || isCountrySpecificCoupon() || hasLocations()) && (
          <Card className='card-container' sx={{ width: '50%' }}>
            <span className='title'>Locations</span>
            {isGlobalCoupon() ? (
              <span className='detail-title'>Available Globally</span>
            ) : isCountrySpecificCoupon() ? (
              <span className='detail-title'>
                Available online in {getUniqueCountries().join(', ')}
              </span>
            ) : (
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {selectedLocations.map((value, index) => {
                  const labelId = `checkbox-list-label-${value.id}`;
                  return (
                    <ListItem
                      key={value.id}
                      secondaryAction={
                        <IconButton edge="end" aria-label="comments">
                        </IconButton>
                      }
                      disablePadding
                    >
                      <ListItemText id={labelId} primary={`${value.address}`} />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Card>
        )}
      </div>
      
      <Card className='card-container' sx={{ width: '100%' }}>
        <span className='title'>Instructions</span>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {instructions.map((value, index) => {
            const labelId = `checkbox-list-label-${value.id}`;

            if (value.checked) {
              return (
                <ListItem
                  key={value.id}
                  secondaryAction={
                    <IconButton edge="end" aria-label="comments">
                    </IconButton>
                  }
                  disablePadding
                >
                  <ListItemText id={labelId} primary={`${value.instruction}`} />
                </ListItem>
              );
            }
          })}
        </List>
      </Card>

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#2b1de8',
            color: '#2b1de8',
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: 'bold',
            '&:hover': {
              borderColor: '#1f16a8',
              backgroundColor: 'rgba(43, 29, 232, 0.1)'
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmClick}
          disabled={isExpired}
          sx={{
            backgroundColor: '#2b1de8',
            color: 'white',
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#1f16a8'
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#666'
            }
          }}
        >
          Confirm Coupon
        </Button>
      </div>
    </div>
  )
}
